import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { WeatherActualPoint } from '@ebalance/proto/bffuser/v1/weather_actual_model_pb'
import { WeatherActualCondition } from '@ebalance/proto/bffuser/v1/weather_actual_model_pb'

import { KOMA_COUNT } from '~/constants/koma'
import { komaIndexToTime } from '~/models/komaToTime'
import { isDaytimeHour } from '~/models/weather/time'
import { weatherActualEmojiOf } from '~/models/weather/vpfd'

/** JST は固定 +9h (DST なし)。実行環境の TZ に依存させない (weather/time.ts と同方針)。 */
const JST_OFFSET_MS = 9 * 60 * 60 * 1000

/** 実績気象の 1 コマ分 (気温・予想気温・露点・降水量。欠損は null)。 */
export type WeatherActualKoma = {
  /** 0-origin コマ番号 (0 = 00:00, 47 = 23:30)。 */
  koma: number
  /** 開始時刻 "HH:MM" (チャートの X 軸キー)。 */
  time: string
  /** 実績気温 (℃)。欠損コマは null。 */
  temperature: number | null
  /**
   * 予想気温 (℃)。気象予報 gRPC のライブ予報由来 (実績とは別系列)。時系列予報の
   * 正時の点のコマにだけ値を持ち、点以外のコマは null (`buildKomaTemperatureForecastValues`
   * 参照、AC-WD-003a)。予想露点は予想湿度が取れないため対象外。
   */
  temperatureForecast: number | null
  /** 実績露点 (℃)。欠損コマは null。 */
  dewPoint: number | null
  /** 実績降水量 (mm)。欠損コマは null。 */
  precipitation: number | null
  /** 実績天気 (晴/曇/雨/雪)。天気アイコン帯の過去区間に使う。欠損コマ・
   *  夜間の晴天など導出不能は UNSPECIFIED。 */
  condition: WeatherActualCondition
}

/** proto google.type.Decimal (= { value: string }) を number へ。undefined は null。 */
function decimalToNumber(d: { value: string } | undefined): number | null {
  if (d === undefined) {
    return null
  }
  const n = Number(d.value)
  return Number.isNaN(n) ? null : n
}

/** proto Timestamp を JST の 0-origin コマ番号へ写す (koma = 時*2 + (分>=30?1:0))。 */
function komaOf(point: WeatherActualPoint): number | null {
  if (point.targetDatetime === undefined) {
    return null
  }
  const jst = new Date(
    timestampDate(point.targetDatetime).getTime() + JST_OFFSET_MS,
  )
  return jst.getUTCHours() * 2 + (jst.getUTCMinutes() >= 30 ? 1 : 0)
}

/**
 * 実績気象 RPC のコマ点列を 48 コマの密な配列へ展開する。バックエンドが
 * 30 分コマに集約済みなので、ここでは target_datetime を JST コマ番号に写して
 * 配置し直すだけ (欠損コマは全 metric null)。同一コマに複数点が来た場合は
 * 後勝ち (バックエンドはコマ一意で返すので実際には発生しない)。
 *
 * `forecastTemps` は気象予報 gRPC 由来の予想気温数値系列 (48 スロット、正時の点の
 * コマだけ値を持つ。`useAreaWeatherIcons` の weatherTempValues)。実績とは別の RPC な
 * ので、コマ番号 (= index) で突き合わせて各コマの temperatureForecast に載せる。
 * 省略時・要素欠落は null (= 予想気温折れ線は描かれない)。
 */
export function buildKomaWeatherActual(
  points: WeatherActualPoint[],
  forecastTemps: ReadonlyArray<number | null> = [],
): WeatherActualKoma[] {
  const series: WeatherActualKoma[] = Array.from(
    { length: KOMA_COUNT },
    (_, koma) => ({
      koma,
      time: komaIndexToTime(koma),
      temperature: null,
      temperatureForecast: forecastTemps[koma] ?? null,
      dewPoint: null,
      precipitation: null,
      condition: WeatherActualCondition.UNSPECIFIED,
    }),
  )

  for (const point of points) {
    const koma = komaOf(point)
    if (koma === null || koma < 0 || koma >= KOMA_COUNT) {
      continue
    }
    series[koma] = {
      koma,
      time: komaIndexToTime(koma),
      temperature: decimalToNumber(point.temperature),
      temperatureForecast: forecastTemps[koma] ?? null,
      dewPoint: decimalToNumber(point.dewPoint),
      precipitation: decimalToNumber(point.precipitation),
      condition: point.condition,
    }
  }

  return series
}

/**
 * いずれかのコマに気象データ (実測気温・露点・降水量、または予想気温) があるか
 * (= 気象チャートを描画するか)。予想気温 (temperatureForecast) も含めるのは、実測
 * ゼロの未来日 (`goToNextDay` で到達可) でも予報 gRPC が返す予想気温の破線を残す
 * ため (spec doji-bg: 気温/予想気温 は同一トグルの 2 系列)。
 *
 * 本関数は「値の有無」だけを判定する。AC-034 の「気象データなし」降格のうち
 * **取得失敗**の検出は呼び出し側の責務 (実績 query の `isError` を併用する) —
 * 合流後の系列だけでは、実績 RPC 失敗 + 予報成功のケースを正常な実測ゼロと
 * 区別できないため。
 */
export function hasAnyWeatherData(series: WeatherActualKoma[]): boolean {
  return series.some(
    (k) =>
      k.temperature !== null ||
      k.dewPoint !== null ||
      k.precipitation !== null ||
      k.temperatureForecast !== null,
  )
}

/** 気温とその天気アイコンの組 (需要ピークの MAX/MIN 行に添える)。 */
export type KomaWeather = {
  /** 実績気温 (℃)。当該コマに実測が無ければ null。 */
  temperature: number | null
  /** 実況天気の絵文字。導出不能・欠測は ''。 */
  emoji: string
}

/**
 * ピークコマ (1 始まり) の実績気温と実況天気アイコンを取り出す。
 *
 * backend が返すコマ番号は 1 始まり、実績気象系列は 0 始まりの密配列なので
 * 添字は `koma - 1`。範囲外 (0 以下 / 49 以上 / 系列が短い) は「気象なし」に
 * 落とす — 気象 RPC と需要 RPC は別系統なので、片方だけ欠けることが実際に
 * 起こる。
 *
 * 天気アイコンはチャートの天気帯と同じ `weatherActualEmojiOf` で実況から出す
 * (= 気温から晴/曇を推定するモックは廃止。晴系の昼夜切替もこちらが持つ)。
 *
 * 需要詳細のサマリーバーと 同時同量一覧のピルバッジ (doji-bg.md AC-BDB-002) が
 * 同じ合成を要るので `~/models/weather/` に置く (旧 BalanceDemandDetail 配下の
 * summaryTemps から昇格)。
 */
export function komaWeatherAt(
  series: readonly WeatherActualKoma[],
  koma: number,
): KomaWeather {
  const index = koma - 1
  const entry = index >= 0 && index < series.length ? series[index] : undefined
  if (entry === undefined) {
    return { temperature: null, emoji: '' }
  }
  // koma は 1 始まりなので時 (0-23) は (koma-1)/2 の整数部。
  const hour = Math.floor(index / 2)
  return {
    temperature: entry.temperature,
    emoji: weatherActualEmojiOf(entry.condition, isDaytimeHour(hour)),
  }
}

/**
 * 天気アイコン帯の 48 コマ絵文字列を「過去=実績アイコン、未来=予報アイコン」で
 * 組み立てる (doji-bg.md AC-BDB-002)。`pastKomaCount` 未満の 0 始まりコマは実況
 * (`weatherActual` の condition) から、それ以降は予報 (`forecastIcons`) から取る。
 * 過去コマに実況が無い (欠測・導出不能) 時は予報で埋めず空にする — 予報で埋めると
 * 気象の確定/未確定の境界が画面から消えるため (design `js/bg-data.js` の天気帯
 * merge と同じ扱い)。3 時間ピッチへの間引きは `buildWeatherStripDisplayIcons`。
 */
export function buildPastActualWeatherIcons(
  forecastIcons: readonly string[],
  weatherActual: readonly WeatherActualKoma[],
  pastKomaCount: number,
): string[] {
  return Array.from({ length: KOMA_COUNT }, (_, i) => {
    if (i >= pastKomaCount) {
      return forecastIcons[i] ?? ''
    }
    return weatherActualEmojiOf(
      weatherActual[i]?.condition ?? WeatherActualCondition.UNSPECIFIED,
      isDaytimeHour(Math.floor(i / 2)),
    )
  })
}
